import { AbstractControl } from '@angular/forms';
import {
    PageSectionConditionModel,
    PageSectionFieldValidator
} from '../../domain/models/dynamic-form-schema.model';

interface NormalizeValidatorOptions {
    forceUniqueTargetKeys?: boolean;
}

type RawValidatorConfig =
    | PageSectionFieldValidator[]
    | Record<string, string>
    | undefined;

export function normalizeValidatorConfigs(
    raw?: RawValidatorConfig,
    options?: NormalizeValidatorOptions
): PageSectionFieldValidator[] {
    if (!raw) return [];

    if (Array.isArray(raw)) {
        return raw.map((validator, index) => ({
            ...validator,
            errorKey: getValidatorErrorKey(validator, options, index)
        }));
    }

    return Object.entries(raw).map(([name, rawValue], index) => {
        const [message, rawArgs] = rawValue.split('|', 2);
        let args: any | undefined;

        if (rawArgs) {
            try {
                args = JSON.parse(rawArgs);
            } catch {
                args = undefined;
            }
        }

        const validator: PageSectionFieldValidator = { name, message, args };

        return {
            ...validator,
            errorKey: getValidatorErrorKey(validator, options, index)
        };
    });
}

export function matchesConditions(
    control: AbstractControl | null | undefined,
    when?: PageSectionConditionModel | PageSectionConditionModel[]
): boolean {
    const conditions = normalizeConditions(when);

    if (!conditions.length) {
        return true;
    }

    return conditions.every((condition) =>
        matchesCondition(control, condition)
    );
}

export function normalizeConditions(
    when?: PageSectionConditionModel | PageSectionConditionModel[]
): PageSectionConditionModel[] {
    if (!when) return [];
    return Array.isArray(when) ? when : [when];
}

export function isValueMissing(value: any): boolean {
    return (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
    );
}

export function getValidatorErrorKey(
    validator: PageSectionFieldValidator,
    options?: NormalizeValidatorOptions,
    index?: number
): string {
    if (validator.errorKey) {
        return validator.errorKey;
    }

    if (options?.forceUniqueTargetKeys && validator.target) {
        return `${validator.name}_${validator.target}_${index ?? 0}`;
    }

    return validator.name;
}

function matchesCondition(
    control: AbstractControl | null | undefined,
    condition: PageSectionConditionModel
): boolean {
    const value = control?.get(condition.field)?.value;

    if (condition.in) {
        return condition.in.includes(value);
    }

    if (condition.notIn) {
        return !condition.notIn.includes(value);
    }

    if (Object.prototype.hasOwnProperty.call(condition, 'equals')) {
        return value === condition.equals;
    }

    if (Object.prototype.hasOwnProperty.call(condition, 'notEquals')) {
        return value !== condition.notEquals;
    }

    if (condition.truthy) {
        return !!value;
    }

    if (condition.falsy) {
        return !value;
    }

    return true;
}
